/**
 * Component renderer factory for success step variants
 */

import React from 'react';
import { Phone, Calendar } from 'lucide-react';
import Button from '../../ui/Button';
import Description from '../../ui/Description';
import PhoneDialer from '../../PhoneDialer';
import { settings } from '../../../config/settings';
import { ComponentType } from './types';

export type ComponentRendererProps = {
  customerPhoneNumber: string;
  onScheduleClick: () => void;
  onCallInitiated: () => void;
  onCountdownComplete: () => void;
  onInitiateCall: () => Promise<{ success: boolean; from?: string; error?: string }>;
  messageTitle?: string;
  messageDescription?: string;
  variantId?: string; // Added to conditionally render icons and styling
  dialerDuration?: number; // Duration in seconds for phone dialer countdown
};

export const createComponentRenderer = (props: ComponentRendererProps) => {
  const {
    customerPhoneNumber,
    onScheduleClick,
    onCallInitiated,
    onCountdownComplete,
    onInitiateCall,
    messageTitle,
    messageDescription,
    variantId,
    dialerDuration,
  } = props;

  const renderers: Record<ComponentType, () => React.ReactNode> = {
    phoneDialer: () => (
      <PhoneDialer
        key="phone-dialer"
        phoneNumber={customerPhoneNumber}
        fallbackNumber={settings.phone.support}
        autoTrigger={true}
        onCallInitiated={onCallInitiated}
        onCountdownComplete={onCountdownComplete}
        onInitiateCall={onInitiateCall}
        duration={dialerDuration} // Pass duration from variant config
      />
    ),

    callButton: () => {
      return (
        <Button
          key="call-button"
          id="success-step-call-support"
          name="success-step-call-support"
          variant="primary"
          size="lg"
          icon={<Phone className="w-5 h-5" />}
          phoneNumber={settings.phone.supportRaw}
          fullWidth
        >
          {settings.phone.support}
        </Button>
      );
    },

    scheduleButton: () => {
      return (
        <Button
          key="schedule-button"
          id="success-step-schedule-appointment"
          name="success-step-schedule-appointment"
          onClick={onScheduleClick}
          variant={'outline'}
          size={'default'}
          icon={<Calendar className="w-5 h-5" />}
          fullWidth
        >
          Schedule Appointment
        </Button>
      );
    },

    message: () => {
      // Special handling for call-initiated variant
      if (variantId === 'call-initiated') {
        return (
          <div key="message" className="text-center animate-fade-in">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {messageTitle}
            </h3>
            {messageDescription && (
              <Description
                id="success-step-message"
                name="success-step-message"
                className="mb-2"
              >
                {messageDescription}
              </Description>
            )}
            <div className="text-2xl font-bold text-[#b3905e] mt-4">
              Incoming call from: {settings.phone.support}
            </div>
          </div>
        );
      }
      
      // Special handling for appointment-scheduled variant
      if (variantId === 'appointment-scheduled') {
        return (
          <div key="message" className="text-center">
            <div className="flex items-center justify-center gap-2 text-[#b3905e] mb-2">
              <Calendar className="w-5 h-5" />
              <span className="font-medium">{messageTitle}</span>
            </div>
            {messageDescription && (
              <Description
                id="success-step-message"
                name="success-step-message"
                className="mb-4"
              >
                {messageDescription}
              </Description>
            )}
          </div>
        );
      }
      
      // Default message rendering
      return (
        <div key="message" className="text-center">
          {messageTitle && (
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {messageTitle}
            </h3>
          )}
          {messageDescription && (
            <Description
              id="success-step-message"
              name="success-step-message"
              className="mb-4"
            >
              {messageDescription}
            </Description>
          )}
        </div>
      );
    },

    missedCallMessage: () => (
      <div key="missed-call-message" className="text-center space-y-4">
        <h3 className="text-2xl font-bold text-gray-900">
          Missed our call?
        </h3>
        <Description
          id="missed-call-description"
          name="missed-call-description"
        >
          You can reach us at:
        </Description>
        <Button
          id="success-step-call-support-missed"
          name="success-step-call-support-missed"
          variant="primary"
          size="lg"
          phoneNumber={settings.phone.supportRaw}
          fullWidth
        >
          {settings.phone.support}
        </Button>
      </div>
    ),
  };

  return (component: ComponentType): React.ReactNode => {
    return renderers[component]();
  };
};
